import { CircleToggleButton } from "../components/CircleToggleButton";
import GroupByAggregateMenu from "../components/GroupByAggregateMenu";
import { useTruncationDetection } from "../hooks/useTruncationDetection";
import * as styles from "../styles";
import {
  GROUP_HEADER_DROPDOWN_ICON_SIZE,
  GROUP_HEADER_SELECT_ICON_PADDING,
} from "../types/constants";
import { getDefaultAggregation } from "../utils/aggregationUtils";
import { allValuesEnumerationKey } from "../utils/rangeGroupingUtils";
import {
  Add as AddIcon,
  ArrowDownward as SortDescIcon,
  ArrowUpward as SortAscIcon,
  DeleteOutline as DeleteOutlineIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { Box, IconButton, MenuItem, Select, Tooltip } from "@mui/material";
import * as labels from "config.labels";
import React, { useEffect, useRef } from "react";

const IndicatorIcons = {
  "sorted-asc": SortAscIcon,
  "sorted-desc": SortDescIcon,
  filtered: FilterListIcon,
};
const IndicatorIcon = (label) => {
  const IndicatorComponent = IndicatorIcons[label];
  return (
    <IndicatorComponent
      key={label}
      sx={{
        fontSize: "1rem",
        opacity: 0.7,
        color: "primary.main",
      }}
    />
  );
};
function renderIndicators({
  isSorted,
  sortDirection,
  sortIndex,
  sortCount,
  hasFilter,
}) {
  let IndicatorIcons = [];
  if (isSorted) {
    const indicatorKey = sortDirection === "asc" ? "sorted-asc" : "sorted-desc";
    IndicatorIcons.push(IndicatorIcon(indicatorKey));
    if (sortCount > 1 && sortIndex != null) {
      IndicatorIcons.push(
        <Box
          key={"sort-index"}
          component={"span"}
          sx={{
            fontSize: "0.625rem",
            fontWeight: 700,
            lineHeight: 1,
            color: "primary.main",
            opacity: 0.7,
            ml: -0.3,
          }}
        >
          {sortIndex + 1}
        </Box>
      );
    }
  }
  if (hasFilter) {
    IndicatorIcons.push(IndicatorIcon("filtered"));
  }
  if (IndicatorIcons.length === 0) return null;
  return (
    <Box
      sx={{
        position: "absolute",
        right: 0,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      {IndicatorIcons.map((icon) => icon)}
    </Box>
  );
}

const COLUMN_TOOLTIP_DELAY = 200;
const ColumnHeaderText = ({ text, margin, onTruncationChange }) => {
  const [ref, isTruncated, evaluateTruncation] = useTruncationDetection();
  const onTruncationChangeRef = useRef(onTruncationChange);
  onTruncationChangeRef.current = onTruncationChange;

  useEffect(() => {
    const frameId = requestAnimationFrame(() => evaluateTruncation());
    return () => cancelAnimationFrame(frameId);
  }, [text, margin, evaluateTruncation]);

  useEffect(() => {
    onTruncationChangeRef.current?.(isTruncated);
  }, [isTruncated]);

  const content = (
    <Box
      component={"span"}
      ref={ref}
      sx={{ ...styles.truncatedText, mr: margin }}
    >
      {text}
    </Box>
  );
  return isTruncated ? (
    <Tooltip title={text} arrow enterDelay={COLUMN_TOOLTIP_DELAY}>
      {content}
    </Tooltip>
  ) : (
    content
  );
};

const GROUP_HEADER_ROW_HEIGHT = 28;

export const createHeaderRenderer = ({
  columnFilters,
  dateColumnFilters = {},
  numberColumnFilters = {},
  filteredColumns,
  sortModel,
  truncatedColumnsRef,
  menuColumn,
  forceUpdate,
  startAdornment,
  onHeaderContextMenu,
  showFilterIndicator = false,
  groupByColumns,
  rangeGroupedColumns,
  showGroupHeader = false,
  columnTypeMap,
  columnAggregations,
  onAggregationChange,
  extraAggregateColumns,
  onAddAggregateColumn,
  onDeleteExtraColumn,
  aggregatorUtils,
  allValuesEnumerations,
  onToggleAllValuesEnumeration,
  summarySelections = {},
  showSummaryHeader = false,
  t,
}) => {
  return (params) => {
    if (!params?.colDef) return null;
    const hasFilter = filteredColumns
      ? filteredColumns.has(params.field)
      : !!columnFilters[params.field] ||
        !!dateColumnFilters[params.field] ||
        !!numberColumnFilters[params.field];
    const sortIndex = sortModel.findIndex((s) => s.field === params.field);
    const sortItem = sortIndex >= 0 ? sortModel[sortIndex] : null;
    const sortDirection = sortItem?.sort ?? null;
    const isSorted = sortDirection !== null;
    const sortCount = sortModel.length;
    const showFilter = showFilterIndicator && hasFilter;
    const hasIndex = isSorted && sortCount > 1;
    const indicatorCount =
      (isSorted ? 1 : 0) + (hasIndex ? 0.5 : 0) + (showFilter ? 1 : 0);
    const textMargin = indicatorCount * 2.5;
    const resolvedAdornment =
      typeof startAdornment === "function"
        ? startAdornment(params)
        : startAdornment;

    const columnNameRow = (
      <>
        {resolvedAdornment ? (
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            {resolvedAdornment}
          </Box>
        ) : null}
        <ColumnHeaderText
          text={params.colDef.headerName ?? ""}
          margin={textMargin}
          onTruncationChange={(truncated) => {
            const hasChanged = truncated
              ? !truncatedColumnsRef.current.has(params.field)
              : truncatedColumnsRef.current.has(params.field);
            if (!hasChanged) return;
            if (truncated) {
              truncatedColumnsRef.current.add(params.field);
            } else {
              truncatedColumnsRef.current.delete(params.field);
            }
            if (menuColumn === params.field) {
              forceUpdate?.();
            }
          }}
        />
        {renderIndicators({
          isSorted,
          sortDirection,
          sortIndex,
          sortCount,
          hasFilter: showFilter,
        })}
      </>
    );

    const summarySelection = summarySelections?.[params.field];
    const summaryText = summarySelection
      ? summarySelection.override !== undefined
        ? `"${summarySelection.override}"`
        : aggregatorUtils?.getFunctionLabel(summarySelection.func) ??
          summarySelection.func
      : "";
    const summaryRow = showSummaryHeader ? (
      <Box
        sx={{
          height: GROUP_HEADER_ROW_HEIGHT,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderTop: "1px solid",
          borderColor: "divider",
          px: 0.5,
          overflow: "hidden",
        }}
      >
        <Box
          component={"span"}
          sx={{
            fontSize: "0.7rem",
            fontWeight: 600,
            color: "secondary.main",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {summaryText}
        </Box>
      </Box>
    ) : null;

    const contextMenuHandler = (event) => {
      if (!onHeaderContextMenu) return;
      event.preventDefault();
      event.stopPropagation();
      onHeaderContextMenu(params, event);
    };

    if (!showGroupHeader) {
      if (showSummaryHeader) {
        return (
          <Box
            onContextMenu={contextMenuHandler}
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "100%",
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                position: "relative",
                px: 0.5,
              }}
            >
              {columnNameRow}
            </Box>
            {summaryRow}
          </Box>
        );
      }
      return (
        <Box
          onContextMenu={contextMenuHandler}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            width: "100%",
            position: "relative",
          }}
        >
          {columnNameRow}
        </Box>
      );
    }

    const isGrouped =
      (groupByColumns?.has(params.field) ||
        rangeGroupedColumns?.has(params.field)) ??
      false;
    const extraColumnEntry = extraAggregateColumns?.find(
      (e) => e.column === params.field
    );
    const isExtraColumn = Boolean(extraColumnEntry);
    const { inputType, inputSubType } = columnTypeMap?.[params.field] ?? {};
    const currentMethod =
      columnAggregations?.[params.field] ??
      getDefaultAggregation(inputType, inputSubType);
    const aggregationOptions =
      aggregatorUtils?.getAggregationOptions(inputType) ?? [];

    return (
      <Box
        onContextMenu={contextMenuHandler}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
        }}
      >
        <Box
          sx={{
            height: GROUP_HEADER_ROW_HEIGHT,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            px: 0.5,
          }}
        >
          {isGrouped ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                px: 0.5,
              }}
            >
              <Box
                component={"span"}
                sx={{
                  flex: 1,
                  textAlign: "center",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: "primary.main",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {t(labels.HEADER_GROUP_BY_INDICATOR, { default: "Group by" })}
              </Box>
              {rangeGroupedColumns?.has(params.field) &&
                onAddAggregateColumn &&
                aggregationOptions.length > 0 && (
                  <GroupByAggregateMenu
                    field={params.field}
                    options={aggregationOptions}
                    onAddAggregateColumn={onAddAggregateColumn}
                  />
                )}
              {(columnTypeMap?.[params.field]?.isEnum ||
                rangeGroupedColumns?.has(params.field)) &&
                onToggleAllValuesEnumeration && (
                  <CircleToggleButton
                    title={t(labels.HEADER_INCLUDE_ALL_ENUM_VALUES, {
                      default: "Include all enum values in group",
                    })}
                    active={
                      allValuesEnumerations?.has(
                        allValuesEnumerationKey(
                          params.field,
                          rangeGroupedColumns
                        )
                      ) ?? false
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleAllValuesEnumeration(
                        allValuesEnumerationKey(
                          params.field,
                          rangeGroupedColumns
                        )
                      );
                    }}
                  />
                )}
            </Box>
          ) : isExtraColumn ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                overflow: "hidden",
              }}
            >
              <Box
                component={"span"}
                sx={{
                  fontSize: "0.7rem",
                  color: "text.secondary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  textAlign: "center",
                }}
              >
                {aggregatorUtils?.getFunctionLabel(extraColumnEntry.func) ??
                  extraColumnEntry.func}
              </Box>
              <Tooltip
                title={t(labels.ADD_AGGREGATE_COLUMN_DELETE, {
                  default: "Remove column",
                })}
              >
                <IconButton
                  size={"small"}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteExtraColumn?.(params.field);
                  }}
                  sx={{ p: "2px", color: "text.secondary", flexShrink: 0 }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Select
              variant={"standard"}
              disableUnderline
              value={currentMethod}
              onChange={(e) => {
                e.stopPropagation();
                onAggregationChange?.(params.field, e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onContextMenu={(e) => e.stopPropagation()}
              sx={{
                width: "100%",
                fontSize: "0.7rem",
                color: "text.secondary",
                "& .MuiSelect-select": {
                  py: 0,
                  pr: `${GROUP_HEADER_SELECT_ICON_PADDING} !important`,
                  textAlign: "center",
                },
                "& .MuiSelect-icon": {
                  fontSize: GROUP_HEADER_DROPDOWN_ICON_SIZE,
                  top: "50%",
                  transform: "translateY(-50%)",
                  right: 0,
                },
              }}
            >
              {aggregationOptions.map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  dense
                  sx={{
                    fontSize: "0.75rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pr: 0.5,
                    gap: 1,
                  }}
                >
                  <Box component={"span"} sx={{ flex: 1 }}>
                    {opt.label}
                  </Box>
                  {opt.value !== currentMethod && (
                    <IconButton
                      size={"small"}
                      aria-label={t(labels.ADD_AGGREGATE_COLUMN_ADD, {
                        default: "Add {name} column",
                        replacements: { name: opt.label },
                      })}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onAddAggregateColumn?.(
                          params.field,
                          opt.value,
                          opt.label
                        );
                      }}
                      sx={{
                        p: "2px",
                        color: "text.secondary",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      <AddIcon sx={{ fontSize: "1.6rem" }} />
                    </IconButton>
                  )}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            position: "relative",
          }}
        >
          {columnNameRow}
        </Box>
        {summaryRow}
      </Box>
    );
  };
};

export const GROUP_HEADER_ROW_HEIGHT_PX = GROUP_HEADER_ROW_HEIGHT;
